import { User, Clock, CheckCircle, XCircle, AlertCircle, Edit } from 'lucide-react';
import { useFormatters } from '../../../contexts/PreferencesContext';

export interface TimelineItem {
  id: number;
  timestamp: string;
  user: string;
  userRole?: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  /** Why the change was made — shown under a "Reason:" label. */
  reason?: string;
  /**
   * What the entry set, when it is not a reason — e.g. the several values one
   * haulage save entered. Shown plainly under the action, with no label.
   */
  detail?: string;
  type: 'create' | 'update' | 'approve' | 'decline' | 'info';
}

interface VerticalTimelineProps {
  items: TimelineItem[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
}

export default function VerticalTimeline({ items, onLoadMore, hasMore = false, loading = false }: VerticalTimelineProps) {
  const getIconAndColor = (type: TimelineItem['type']) => {
    switch (type) {
      case 'create':
        return { icon: CheckCircle, color: 'text-blue-600 bg-blue-100', lineColor: 'bg-blue-300' };
      case 'update':
        return { icon: Edit, color: 'text-yellow-600 bg-yellow-100', lineColor: 'bg-yellow-300' };
      case 'approve':
        return { icon: CheckCircle, color: 'text-green-600 bg-green-100', lineColor: 'bg-green-300' };
      case 'decline':
        return { icon: XCircle, color: 'text-red-600 bg-red-100', lineColor: 'bg-red-300' };
      case 'info':
      default:
        return { icon: AlertCircle, color: 'text-gray-600 bg-gray-100', lineColor: 'bg-gray-300' };
    }
  };

  // CR-6307: preference-aware date/time formatting (was hardcoded en-US).
  const { formatDateTime } = useFormatters();

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No timeline history available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline Container */}
      <div className="space-y-6">
        {items.map((item, index) => {
          const { icon: Icon, color, lineColor: _lineColor } = getIconAndColor(item.type);
          const isLast = index === items.length - 1;

          return (
            <div key={item.id} className="relative flex gap-4">
              {/* Timeline Line */}
              {!isLast && (
                <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-border" />
              )}

              {/* Icon Container */}
              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${color} flex-shrink-0`}>
                <Icon className="h-4 w-4" />
              </div>

              {/* Content */}
              <div className="flex-1 pb-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span className="font-semibold text-sm">
                        {item.user}
                        {item.userRole && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">({item.userRole})</span>
                        )}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{item.action}</p>
                    {item.detail && (
                      <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatDateTime(item.timestamp)}
                  </div>
                </div>

                {/* Field Change Details */}
                {item.field && (item.oldValue || item.newValue) && (
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg border">
                    <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                      {item.field.replace(/_/g, ' ')}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {item.oldValue && (
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Previous:</div>
                          <div className="text-sm font-medium text-red-700 bg-red-50 px-2 py-1 rounded border border-red-200">
                            {item.oldValue}
                          </div>
                        </div>
                      )}
                      {item.newValue && (
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">New:</div>
                          <div className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200">
                            {item.newValue}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reason */}
                {item.reason && (
                  <div className="mt-2 text-sm italic text-muted-foreground bg-muted/20 px-3 py-2 rounded border-l-2 border-muted-foreground/30">
                    <span className="font-medium not-italic">Reason: </span>
                    {item.reason}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="mt-6 text-center">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Loading...' : 'Load More History'}
          </button>
        </div>
      )}
    </div>
  );
}
